import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocale } from '@adsk/alloy-react-helpers';
import Scrollparent from 'scrollparent';
import { createGeocoder, buildAddress } from './utils';
import { GeocoderWrapperStyled } from './styles';

const AutocompleteAddress = ({
  autocompletedAddress,
  defaultCountry,
  disabled,
  isInModal,
  mapBoxAccessToken,
  onAddressChange,
  onActivateMapboxToken,
  onFailedActivateMapboxToken,
  onResult,
  placeholder,
  resetAddressOnDelete,
}) => {
  const { T, localeName } = useLocale(({ locale }) =>
    require(`./i18n/${locale}.json`)
  );
  const [geocoder, setGeocoder] = useState(null);
  const [scrollParent, setScrollParent] = useState(null);
  const [scrollParentOffset, setScrollParentOffset] = useState(0);
  const geocoderWrapperRef = useRef(null);
  // Keep the onAddressChange latest value
  const latestOnAddressChange = useRef(onAddressChange);
  // Keep the onResult latest value
  const latestOnResult = useRef(onResult);
  // Keep the autocompletedAddress latest value
  const latestAutocompletedAddress = useRef(autocompletedAddress);
  const latestOnActivateMapbox = useRef(onActivateMapboxToken);
  const latestOnFailedActivateMapbox = useRef(onFailedActivateMapboxToken);

  useEffect(() => {
    latestOnAddressChange.current = onAddressChange;
  }, [onAddressChange]);

  useEffect(() => {
    latestOnResult.current = onResult;
  }, [onResult]);

  useEffect(() => {
    latestAutocompletedAddress.current = autocompletedAddress;
  }, [autocompletedAddress]);

  useEffect(() => {
    latestOnActivateMapbox.current = onActivateMapboxToken;
  }, [onActivateMapboxToken]);

  useEffect(() => {
    latestOnFailedActivateMapbox.current = onFailedActivateMapboxToken;
  }, [onFailedActivateMapboxToken]);

  // Find scrolling parent to later adjust suggestions position
  useEffect(() => {
    setScrollParent(Scrollparent(geocoderWrapperRef.current));
  }, []);

  // When scrolling parent is found, bind to scrolling event
  useEffect(() => {
    if (scrollParent === null) return undefined;

    const handleScroll = () => {
      setScrollParentOffset(scrollParent.scrollTop);
    };

    scrollParent.addEventListener('scroll', handleScroll);

    return () => {
      scrollParent.removeEventListener('scroll', handleScroll);
    };
  }, [scrollParent]);

  // Geocoder component creation/removal
  useEffect(() => {
    try {
      const newGeocoder = createGeocoder(
        mapBoxAccessToken,
        placeholder,
        localeName
      );

      newGeocoder._renderNoResults = function _renderNoResults() {
        const errorMessage = `<div class='mapbox-gl-geocoder--error mapbox-gl-geocoder--no-results'>${T.translate(
          'NO_RESULTS'
        )}</div>`;
        // eslint-disable-next-line react/no-this-in-sfc
        this._renderMessage(errorMessage);
      };

      // The following block removes the "Powered by Mapbox" footer introduced in https://github.com/mapbox/mapbox-gl-geocoder/pull/438
      newGeocoder.on('results', function callback() {
        newGeocoder._hideAttribution();
      });
      newGeocoder.addTo('#geocoder_wrapper');
      setGeocoder(newGeocoder);
      latestOnActivateMapbox.current();
    } catch (e) {
      // No action required - mapbox token not active forces the manual input flow
      // eslint-disable-next-line no-console
      console.warn(
        'Warning: Unable to get valid mapbox token, defaulting to ',
        'manual address input flow'
      );
      latestOnFailedActivateMapbox.current();
    }

    return () => {
      const geoWrapper = document.getElementById('geocoder_wrapper');
      if (geoWrapper) geoWrapper.innerHTML = '';
    };
  }, [localeName, placeholder, mapBoxAccessToken, defaultCountry, T]);

  // Geocoder event handlers binding/unbinding
  useEffect(() => {
    const handleResult = ({ result }) => {
      latestOnResult.current(result.place_name);
      latestOnAddressChange.current(buildAddress(result), true);
    };

    const handleBlur = () => {
      const geocoderInput = geocoder._inputEl;

      if (geocoderInput.value !== latestAutocompletedAddress.current) {
        if (geocoderInput.value === '' && resetAddressOnDelete) {
          latestOnResult.current('');
        } else if (latestAutocompletedAddress.current === '') {
          geocoder.setInput(latestAutocompletedAddress.current);
        } else {
          geocoderInput.value = latestAutocompletedAddress.current;
        }
      }
    };

    const bindHandlers = () => {
      geocoder.on('result', handleResult);
      geocoder._inputEl.addEventListener('blur', handleBlur);
    };

    const unbindHandlers = () => {
      geocoder.off('result', handleResult);
      geocoder._inputEl.removeEventListener('blur', handleBlur);
    };

    if (!geocoder) return undefined;
    bindHandlers();

    return () => {
      unbindHandlers();
    };
  }, [geocoder, resetAddressOnDelete]);

  // Change geocoder input value when autocompleted address changes
  useEffect(() => {
    if (!geocoder) return;

    const geocoderInput = geocoder._inputEl;

    if (autocompletedAddress === '' && geocoderInput.value !== '') {
      geocoder.clear();

      return;
    }

    geocoderInput.value = autocompletedAddress;
  }, [autocompletedAddress, geocoder]);

  // Geocoder disabled property change
  useEffect(() => {
    if (!geocoder) return;

    geocoder._inputEl.disabled = disabled;
  }, [disabled, geocoder]);

  useEffect(() => {
    if (!geocoder || !defaultCountry) return;

    geocoder.setCountries(defaultCountry);
  }, [defaultCountry, geocoder]);

  return (
    <GeocoderWrapperStyled
      id="geocoder_wrapper"
      isInModal={isInModal}
      ref={geocoderWrapperRef}
      suggestionsOffset={scrollParentOffset}
      suggestionsOffsetWidth={
        geocoderWrapperRef.current
          ? `${geocoderWrapperRef.current.offsetWidth}px`
          : 'auto'
      }
    />
  );
};

AutocompleteAddress.propTypes = {
  autocompletedAddress: PropTypes.string.isRequired,
  defaultCountry: PropTypes.string,
  disabled: PropTypes.bool,
  isInModal: PropTypes.bool,
  mapBoxAccessToken: PropTypes.string.isRequired,
  onActivateMapboxToken: PropTypes.func.isRequired,
  onAddressChange: PropTypes.func.isRequired,
  onFailedActivateMapboxToken: PropTypes.func.isRequired,
  onResult: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  resetAddressOnDelete: PropTypes.bool.isRequired,
};

AutocompleteAddress.defaultProps = {
  defaultCountry: '',
  disabled: false,
  isInModal: false,
};

export default AutocompleteAddress;
